import { useCallback, useEffect } from "react";
import type { EmblaCarouselType } from "embla-carousel";

/**
 * Applies discrete opacity tiers based on distance from the center slide.
 * Figma spec: center 3 cards at 1.0, adjacent pair at 0.4, edges at 0.2.
 * No scale — Figma uses same-size cards with opacity-only differentiation.
 */
export function useCarouselOpacity(emblaApi: EmblaCarouselType | undefined) {
  const updateTransforms = useCallback(() => {
    if (!emblaApi) return;
    const scrollProgress = emblaApi.scrollProgress();
    const snapList = emblaApi.scrollSnapList();

    snapList.forEach((snap, index) => {
      let distance = Math.abs(scrollProgress - snap);

      // Handle loop wraparound
      if (distance > 0.5) distance = 1 - distance;

      // Convert to slide-index units so thresholds are slide-count-independent
      const slideDistance = distance * snapList.length;

      let opacity: number;

      if (slideDistance <= 1.2) {
        opacity = 1.0; // center 3 cards
      } else if (slideDistance <= 2.2) {
        opacity = 0.4; // adjacent pair
      } else {
        opacity = 0.2; // edges
      }

      const slideNode = emblaApi.slideNodes()[index];
      const inner = slideNode?.querySelector<HTMLElement>(
        "[data-scale-target]",
      );
      if (inner) {
        inner.style.transform = "";
        inner.style.opacity = String(opacity);
      }
    });
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("scroll", updateTransforms);
    emblaApi.on("reInit", updateTransforms);
    updateTransforms();
    return () => {
      emblaApi.off("scroll", updateTransforms);
      emblaApi.off("reInit", updateTransforms);
    };
  }, [emblaApi, updateTransforms]);
}
